import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StarIcon from '@mui/icons-material/Star';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
// MUI 6 ships the responsive `size` API as Grid2; plain `Grid` is still the v1
// `item`/`xs` component. Importing the wrong one type-errors on every `size`.
import Grid from '@mui/material/Grid2';
import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { ApiError } from '@/api/client';
import { fetchCustomer, formatMinor, formatTerms } from '@/api/commercial';
import type { CustomerAddressData, CustomerContactData } from '@/types/generated';

/**
 * One customer, with its contacts and typed addresses.
 *
 * The detail endpoint is the only one that returns those relations; the list
 * endpoint omits them rather than sending empty arrays a caller might read as
 * "this customer has none".
 */
export function CustomerDetailPage() {
  const { id = '' } = useParams<{ id: string }>();

  const query = useQuery({
    queryKey: ['customer', id],
    queryFn: () => fetchCustomer(id),
    enabled: id !== '',
  });

  if (query.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (query.isError) {
    // 404 is what another tenant's customer looks like, deliberately — a 403
    // would confirm the record exists. So "not found" is the honest message for
    // both cases.
    const status = query.error instanceof ApiError ? query.error.status : 0;

    return (
      <Stack spacing={2}>
        <Alert severity={status === 404 ? 'info' : 'error'}>
          {status === 404 ? 'That customer does not exist.' : 'Could not load this customer.'}
        </Alert>
        <BackLink />
      </Stack>
    );
  }

  const customer = query.data;

  return (
    <Stack spacing={2}>
      <BackLink />

      <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap">
        <Typography variant="h5">{customer.name}</Typography>
        {customer.code && (
          <Typography variant="body2" color="text.secondary">
            {customer.code}
          </Typography>
        )}
        <Chip size="small" label={customer.tier} />
        <Chip
          size="small"
          variant="outlined"
          label={customer.status}
          color={customer.allows_new_business ? 'success' : 'warning'}
        />
      </Stack>

      {!customer.allows_new_business && (
        // Worth stating rather than leaving to be inferred from a chip: this is
        // the reason a salesperson cannot raise an RFQ for them (M6).
        <Alert severity="warning">
          This customer is {customer.status.replace('_', ' ')}, so no new quotations can be raised.
          Existing quotes are unaffected.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Commercial
              </Typography>
              <Field label="Legal name" value={customer.legal_name ?? '—'} />
              <Field label="Industry" value={customer.industry} />
              <Field
                label="Credit limit"
                value={formatMinor(customer.credit_limit_minor, customer.default_currency)}
              />
              <Field label="Payment terms" value={formatTerms(customer.credit_terms_days)} />
              <Field label="Currency" value={customer.default_currency} />
              <Field label="Country" value={customer.country} />
              <Field
                label="Tax"
                value={
                  customer.is_tax_exempt
                    ? 'Exempt'
                    : (customer.tax_identifier ?? 'No identifier recorded')
                }
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Contacts
              </Typography>

              {contactList(customer.contacts)}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Addresses
              </Typography>

              {addressList(customer.addresses)}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {customer.notes && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Notes
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {customer.notes}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}

/**
 * `contacts` is Optional on the PHP DTO, and the generated type says so exactly:
 * `undefined | CustomerContactData[]`. It is absent on the list endpoint and
 * present on the detail endpoint, so the undefined case is handled rather than
 * assumed away.
 *
 * Typed as the generated union rather than `| unknown` — `T | unknown` collapses to
 * `unknown`, which silently turns every element access back into `any`.
 */
function contactList(contacts: CustomerContactData[] | undefined): ReactElement {
  if (contacts === undefined || contacts.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No contacts recorded.
      </Typography>
    );
  }

  return (
    <Stack divider={<Divider flexItem />} spacing={1}>
      {contacts.map((contact) => (
        <Stack key={contact.id} direction="row" spacing={1} alignItems="center">
          {contact.is_primary && <StarIcon fontSize="small" color="warning" titleAccess="Primary contact" />}
          <Box>
            <Typography variant="body2">
              {contact.name}
              {contact.job_title ? ` — ${contact.job_title}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {[contact.email, contact.phone].filter(Boolean).join(' · ') || 'No contact details'}
            </Typography>
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}

function addressList(addresses: CustomerAddressData[] | undefined): ReactElement {
  if (addresses === undefined || addresses.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No addresses recorded.
      </Typography>
    );
  }

  return (
    <Grid container spacing={1.5}>
      {addresses.map((address) => (
        <Grid key={address.id} size={{ xs: 12, sm: 6, md: 4 }}>
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Chip size="small" label={address.type} variant="outlined" />
              {address.is_default && <Chip size="small" label="default" color="success" />}
            </Stack>
            {address.label && <Typography variant="caption">{address.label}</Typography>}
            {/* Joined server-side, so every client renders it identically. */}
            <Typography variant="body2">{address.single_line}</Typography>
          </Stack>
        </Grid>
      ))}
    </Grid>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.4 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
}

function BackLink() {
  return (
    <Box>
      <Button component={RouterLink} to="/customers" size="small" startIcon={<ArrowBackIcon />}>
        All customers
      </Button>
    </Box>
  );
}
